import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { AES, enc } from "crypto-js";
import { toast } from "react-toastify";
import Modal from "react-modal";
import { ThreeDots } from 'react-loader-spinner';
import Currencies from '../../../constants/currencies';
import { setHeaderText } from '../../../features/headerText/headerTextSlice';

const EditPlan = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { encryptedPlanId } = useParams(); // Get the plan ID from URL
  const decodedId = decodeURIComponent(encryptedPlanId.replace(/%2F/g, "/"));
  const decryptedBytes = AES.decrypt(decodedId, "encryptionKey");
  const id = decryptedBytes.toString(enc.Utf8);
  const token = useSelector((state) => state.masterAuth.masterUser.token);

  const [plan, setPlan] = useState({
    name: '',
    description: '',
    currency: '',
    trial_period: '',
    monthlyPrice: '',
    yearlyprice: '',
    features: [],
    duration: '',
    status: ''
  });
  const [loading, setLoading] = useState(true);
  const [currenciesList, setCurrenciesList] = useState([]);
  const [currencyCodeSearchTerm, setCurrencyCodeSearchTerm] = useState('');
  const currencyDropdownRef = useRef(null);
  const [currencyCodeDropdownOpen, setCurrencyCodeDropdownOpen] = useState(false);
  const currencyCodeRef = useRef(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // Fetch plan data when component mounts
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const response = await fetch(`/api/subscriptions/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        setPlan({
          name: data.name || '',
          description: data.description || '',
          currency: data.currency || '',
          trial_period: data.trial_period || '',
          monthlyPrice: data.monthlyPrice || '',
          yearlyprice: data.yearlyprice || '',
          features: data.features || [],
          duration: data.duration || '',
          status: data.status || ''
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching plan:', error);
        setLoading(false);
      }
    };

    const sortedCurrencies = Currencies.sourceCurrencies[0].targetCurrencies.slice()
      .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode));
    setCurrenciesList(sortedCurrencies);
    dispatch(setHeaderText("Edit Subscription"));
    fetchPlan();

    const handleOutsideClick = (event) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target)) {
        setCurrencyCodeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [id, token, dispatch]);

  // Handlers
  const handleFieldChange = (field) => (e) => {
    setPlan({ ...plan, [field]: e.target.value });
  };

  const handleFeatureChange = (e, index) => {
    const updatedFeatures = [...plan.features];
    updatedFeatures[index] = e.target.value;
    setPlan({ ...plan, features: updatedFeatures });
  };

  const handleAddFeature = (e) => {
    e.preventDefault();
    setPlan({ ...plan, features: [...plan.features, ''] });
  };

  const handleDeleteFeature = (index) => {
    const updatedFeatures = plan.features.filter((_, i) => i !== index);
    setPlan({ ...plan, features: updatedFeatures });
  };

  const handleToggleCurrencyDropdown = () => {
    setCurrencyCodeDropdownOpen((prevOpen) => !prevOpen);
  };

  const handleCurrencySearchInputChange = (e) => {
    setCurrencyCodeSearchTerm(e.target.value);
  };

  const filteredCurrency = currenciesList.filter((currency) => {
    const currencyName = `${currency.currencyCode} (${currency.currencyName})`;
    return currencyName?.toLowerCase().includes(currencyCodeSearchTerm?.toLowerCase());
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!plan.name || !plan.description || !plan.currency || !plan.trial_period ||
      !plan.monthlyPrice || !plan.yearlyprice || plan.features.length === 0) {
      toast.error("All fields are required", { position: "bottom-right", autoClose: 2000 });
      return;
    }

    try {
      const response = await fetch(`/api/master-user/subscriptions/edit/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(plan)
      });

      if (response.ok) {
        toast.success('Plan updated successfully!', { position: "bottom-right", autoClose: 2000 });
        navigate("/master-user/subscriptions", { state: { fromEditOrAdd: true } });
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to update plan', { position: "bottom-right", autoClose: 2000 });
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Something went wrong', { position: "bottom-right", autoClose: 2000 });
    }
  };

  const handleOpenDeleteModal = (e) => {
    e.preventDefault();
    setDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setDeleteModalOpen(false);
    setIsSubmitLoading(false);
  };

  const handleConfirmDelete = async () => {
    setIsSubmitLoading(true);
    try {
      const response = await fetch(`/api/subscriptions/delete/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Plan deleted successfully!', { position: "bottom-right", autoClose: 2000 });
        setDeleteModalOpen(false);
        navigate("/master-user/subscriptions", { state: { fromEditOrAdd: true } });
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to delete plan', { position: "bottom-right", autoClose: 2000 });
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Something went wrong', { position: "bottom-right", autoClose: 2000 });
    } finally {
      setIsSubmitLoading(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className='plan-edit-outer'>
      <div className='plan-edit-top'>
        <div className='top-left-edit'>
          {plan.name}
        </div>
        <div className='top-right-edit'>
          <button onClick={() => navigate("/master-user/subscriptions")}>
            <span>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 11.5L11.5 1" stroke="#282828" />
                <path d="M1 1L11.5 11.5" stroke="#282828" />
              </svg>
            </span>
            Cancel
          </button>
        </div>
      </div>
      <div className='plan-edit-inner'>
        <form>
          <div className='plan-edit-inner-top'>
            <div className='plan-edit-inner-top-left'>
              <label>
                Name:
                <input
                  type="text"
                  value={plan.name}
                  onChange={handleFieldChange('name')}
                />
              </label>
            </div>
            <div className='plan-edit-inner-top-right'>
              <label>
                Monthly Pricing:
                <input
                  type="number"
                  value={plan.monthlyPrice}
                  onChange={handleFieldChange('monthlyPrice')}
                />
              </label>
              <label>
                Yearly Pricing:
                <input
                  type="number"
                  value={plan.yearlyprice}
                  onChange={handleFieldChange('yearlyprice')}
                />
              </label>
            </div>
          </div>

          <div className='plan-edit-inner-top'>
            <div className='plan-edit-inner-top-right'>
              <div className="form-field-client">
                <label htmlFor="currency" className="form-label">
                  Currency
                </label>
                <div ref={currencyDropdownRef} className="select-modal-mainM custom-dropdown-user-new-outer">
                  <span
                    id="currency"
                    onClick={handleToggleCurrencyDropdown}
                    className="custom-dropdown-user-new"
                    ref={currencyCodeRef}
                  >
                    {plan.currency || 'Select Currency'}
                  </span>
                  {currencyCodeDropdownOpen && (
                    <div className="custom-dropdown-new">
                      <input
                        type="text"
                        className="search-bar"
                        placeholder="Search"
                        value={currencyCodeSearchTerm}
                        onChange={handleCurrencySearchInputChange}
                      />
                      <ul className="country-list">
                        {filteredCurrency.map((c) => (
                          <li
                            key={c.currencyCode}
                            className={plan.currency === c.currencyCode ? 'selected-item' : ''}
                            onClick={() => {
                              setPlan({ ...plan, currency: c.currencyCode });
                              setCurrencyCodeDropdownOpen(false);
                            }}
                          >
                            {`${c.currencyCode} (${c.currencyName})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <label>
                Trial period:
                <input
                  min={0}
                  type="number"
                  value={plan.trial_period}
                  onChange={handleFieldChange('trial_period')}
                />
              </label>

              <label>
                Duration:
                <select
                  value={plan.duration}
                  onChange={handleFieldChange('duration')}
                >
                  <option value="daily">Day</option>
                  <option value="weekly">Week</option>
                  <option value="monthly">Month</option>
                  <option value="yearly">Year</option>
                </select>
              </label>

              <label>
                Status:
                <select
                  value={plan.status}
                  onChange={handleFieldChange('status')}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>

              <label>
                Description:
                <input
                  type="text"
                  value={plan.description}
                  onChange={handleFieldChange('description')}
                />
              </label>
            </div>
          </div>

          <div className='feature-edit-subscription'>
            <div className='feature-edit-title'>Plan features</div>
            <ul>
              {plan.features.map((feature, index) => (
                <li key={index}>
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleFeatureChange(e, index)}
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteFeature(index)}
                    style={{ marginLeft: '10px', color: 'red' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" fill="none">
                      {/* SVG paths remain the same as in AddPlan */}
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
            <button className="add-feature" onClick={handleAddFeature}>
              <span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M6.75 0V13.5" stroke="#282828" />
                  <path d="M13.5 6.75L0 6.75" stroke="#282828" />
                </svg>
              </span>
              Add Item
            </button>
          </div>
        </form>
      </div>
      <div className="plan-edit-inner-bottom">
        <button onClick={handleOpenDeleteModal} className="edit-delete">
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" fill="none">
              <path d="M16.9577 19.6445L17.4363 4.37763H19.5772V3.85209H14.0109V2.45942C14.0109 1.69739 13.4316 1.09302 12.7011 1.09302H7.966C7.23557 1.09302 6.65628 1.69739 6.65628 2.45942V3.85209H1.08997V4.37763H3.23085L3.70941 19.6445C3.73459 20.354 4.2887 20.9058 4.96875 20.9058H15.6732C16.3784 20.9058 16.9325 20.354 16.9577 19.6445ZM7.16001 2.45942C7.16001 1.98643 7.51263 1.61856 7.966 1.61856H12.7011C13.1545 1.61856 13.5071 1.98643 13.5071 2.45942V3.85209H7.16001V2.45942ZM4.21314 19.6182L3.73459 4.37763H16.9325L16.454 19.6182C16.4288 20.0387 16.1014 20.3803 15.6984 20.3803H4.96875C4.56576 20.3803 4.23833 20.0387 4.21314 19.6182Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M15.6736 21H4.96874C4.23877 21 3.64643 20.4064 3.61944 19.6486L3.14359 4.4723H1V3.75907H6.56631V2.46024C6.56631 1.64144 7.18114 1 7.96598 1H12.7011C13.486 1 14.1008 1.64144 14.1008 2.46024V3.75907H19.6671V4.4723H17.5231L17.0504 19.5515H17.0508L17.0472 19.6486C17.0202 20.4064 16.4166 21 15.6732 21H15.6736ZM1.17991 4.28414H3.3181L3.79935 19.6416C3.82273 20.2976 4.33637 20.8118 4.96874 20.8118H15.6736C16.3267 20.8118 16.8376 20.3149 16.8673 19.6538V19.6416L17.349 4.28414H19.4872V3.9463H13.9209V2.45978C13.9209 1.74608 13.3852 1.18722 12.7011 1.18722H7.96598C7.28189 1.18722 6.74622 1.74608 6.74622 2.45978V3.9463H1.17991V4.28414ZM4.96874 20.4745C4.52347 20.4745 4.15196 20.101 4.12363 19.6242V19.6214L3.64193 4.28367H17.0256L16.5439 19.6214C16.5151 20.101 16.1441 20.474 15.6984 20.474H4.96874V20.4745ZM4.30308 19.6139C4.32647 19.9912 4.61837 20.2868 4.96874 20.2868H15.6984C16.0492 20.2868 16.3415 19.9907 16.364 19.6125L16.8394 4.47137H3.82723L4.30263 19.6134L4.30308 19.6139ZM13.5971 3.9463H7.07005V2.45978C7.07005 1.93565 7.46359 1.52507 7.96598 1.52507H12.7011C13.2035 1.52507 13.5971 1.93565 13.5971 2.45978V3.9463ZM7.24995 3.7586H13.4172V2.45978C13.4172 2.04075 13.1028 1.71276 12.7011 1.71276H7.96598C7.56434 1.71276 7.24995 2.04075 7.24995 2.45978V3.7586Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M10.0811 8.37158H10.5848V16.2284H10.0811V8.37158Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M10.6747 16.3223H9.99109V8.27783H10.6747V16.3223ZM10.171 16.1346H10.4948V8.46552H10.171V16.1346Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M7.05884 8.37158H7.56258V16.2284H7.05884V8.37158Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M7.65252 16.3223H6.96887V8.27783H7.65252V16.3223ZM7.14878 16.1346H7.47261V8.46552H7.14878V16.1346Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M13.1046 8.37158H13.6084V16.2284H13.1046V8.37158Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M13.6983 16.3223H13.0146V8.27783H13.6983V16.3223ZM13.1946 16.1346H13.5184V8.46552H13.1946V16.1346Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
            </svg>
          </span>
          Delete Plan
        </button>
        <button onClick={handleSave} className="edit-save">Save Changes</button>
      </div>
      <Modal
        className="delete-modal"
        isOpen={deleteModalOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Confirm Delete"
        style={{
          content: {
            height: "41%",
            width: "30%",
            position: "fixed",
            top: "36%",
            left: "50%",
            transform: "translate(-24.75%, 0%)",
            parent: document.querySelector(".admin-outer.time.tracker"),
          },
        }}
      >
        <div className="delete-modal-content">
          <h2>Confirm Delete</h2>
          <p>Are you sure you want to delete this plan?</p>
          {isSubmitLoading ? (
            <div className="outter-load-table">
              <ThreeDots
                height="38"
                width="40"
                radius="9"
                color="#6479f9"
                ariaLabel="three-dots-loading"
                wrapperStyle={{}}
                wrapperClassName=""
                visible={true}
              />
            </div>
          ) : (
            <div className="delete-modal-buttons">
              <button className="delete" onClick={handleConfirmDelete} disabled={isSubmitLoading}>
                Delete
              </button>
              <button onClick={handleCloseModal}>Close</button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default EditPlan;